import { useTranslation } from "@sagot/i18n";
import { Button, Icon } from "@sagot/ui";
import type { AddStepEntry, TriggerLock } from "./add-step-catalog";
import { AddStepItem, optionIdOf } from "./add-step-item";
import type { AddStepSectionModel } from "./add-step-model";

const S = "engage.workflows.addStep";
const LOCK_COPY: Record<TriggerLock, string> = {
  visitor: "engage.workflows.stepGroups.visitorLocked",
  wait: "engage.workflows.stepGroups.waitLocked",
  conversation: `${S}.locks.conversation`,
  system: "engage.workflows.defaults.builder.lockedStep",
  service: `${S}.locks.service`,
};

interface AddStepSectionProps {
  section: AddStepSectionModel;
  panelId: string;
  /** While a search is typed the sections are plain headings: nothing folds and "View more" is not needed. */
  searching: boolean;
  open: boolean;
  expanded: boolean;
  activeId: string | null;
  onToggle: () => void;
  onToggleExpanded: () => void;
  onActivate: (optionId: string) => void;
  onChoose: (entry: AddStepEntry) => void;
}

/** The reasons the trigger locks some of the steps shown, each said once for the section. */
function lockReasons(entries: readonly AddStepEntry[]): TriggerLock[] {
  const found = new Set<TriggerLock>();
  for (const entry of entries) if (entry.triggerLock) found.add(entry.triggerLock);
  return [...found];
}

/** One heading with its steps. The heading folds the list (a button with `aria-expanded`); the list is its own listbox. */
export function AddStepSection({ section, panelId, searching, open, expanded, activeId, onToggle, onToggleExpanded, onActivate, onChoose }: AddStepSectionProps) {
  const { t } = useTranslation();
  const headingId = `${panelId}-${section.id}-heading`;
  const listId = `${panelId}-${section.id}-list`;
  const moreId = `${panelId}-${section.id}-more`;
  const title = t(`${S}.sections.${section.id}`);
  const showRest = expanded && !searching && section.rest.length > 0;
  const shown = showRest ? [...section.visible, ...section.rest] : section.visible;
  const reasons = lockReasons(shown);
  const noteId = (reason: TriggerLock) => `${panelId}-${section.id}-note-${reason}`;
  const item = (entry: AddStepEntry) => {
    const optionId = optionIdOf(panelId, section.id, entry.id);
    return (
      <AddStepItem
        key={entry.id}
        entry={entry}
        optionId={optionId}
        active={optionId === activeId}
        noteId={entry.triggerLock ? noteId(entry.triggerLock) : undefined}
        onActivate={() => { onActivate(optionId); }}
        onChoose={() => { onChoose(entry); }}
      />
    );
  };
  return (
    <section className="py-1" data-section={section.id}>
      <h3 id={headingId} className="px-1 text-xs font-semibold text-fg-muted">
        {searching ? (
          <span className="flex h-8 items-center px-1">{title}</span>
        ) : (
          <button
            type="button"
            aria-expanded={open}
            aria-controls={listId}
            className="flex h-8 w-full items-center gap-1.5 rounded-md px-1 text-left hover:bg-sunken"
            onClick={onToggle}
          >
            <Icon name={open ? "chevron-down" : "chevron-right"} className="size-3.5" />
            {title}
          </button>
        )}
      </h3>
      {open
        ? reasons.map((reason) => (
            <p key={reason} id={noteId(reason)} className="px-3 pb-1.5 text-xs text-fg-subtle">
              {t(LOCK_COPY[reason])}
            </p>
          ))
        : null}
      <div id={listId} role="listbox" aria-labelledby={headingId} hidden={!open} className="px-1">
        {section.visible.map(item)}
      </div>
      {open && showRest ? (
        // The rest of the list scrolls, so it takes focus itself (a scrollable region must be reachable without a mouse);
        // the combobox still moves the highlight through its options with the arrow keys and scrolls them into view.
        <div
          id={moreId}
          role="listbox"
          tabIndex={0}
          aria-label={t(`${S}.moreOf`, { section: title })}
          className="scrollbar-visible mx-1 max-h-56 overflow-y-auto rounded-md outline-offset-[-2px]"
          data-testid={`add-step-more-${section.id}`}
        >
          {section.rest.map(item)}
        </div>
      ) : null}
      {open && !searching && section.rest.length > 0 ? (
        <Button type="button" variant="ghost" size="sm" className="ml-1 mt-1" aria-expanded={expanded} aria-controls={moreId} onClick={onToggleExpanded}>
          <Icon name={expanded ? "chevron-up" : "chevron-down"} />
          {expanded ? t(`${S}.viewLess`) : t(`${S}.viewMore`, { count: section.rest.length })}
        </Button>
      ) : null}
    </section>
  );
}
